import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShieldCheck, FlaskConical, Truck, Minus, Plus, ArrowLeft, CheckCircle2, Beaker } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Vial } from "@/components/ProductCard";
import { baseName } from "@/lib/group";
import { LEGAL } from "@/lib/brand";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [all, setAll] = useState([]);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();
  const nav = useNavigate();

  useEffect(() => { api.get(`/products/${slug}`).then(({ data }) => setP(data)).catch(() => setP(false)); setQty(1); }, [slug]);
  useEffect(() => { api.get("/products").then(({ data }) => setAll(data)).catch(() => {}); }, []);

  if (p === false) return <div className="max-w-7xl mx-auto px-6 py-32 text-center font-display text-3xl">PRODUCT NOT FOUND</div>;
  if (!p) return <div className="min-h-[60vh]" />;

  const variants = p.group ? all.filter((x) => x.group === p.group).sort((a, b) => a.price - b.price) : [];
  const grouped = variants.length > 1;
  const title = grouped ? baseName(p.name) : p.name;

  const buyNow = () => { addItem(p, qty); nav("/cart"); };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
        <Link to="/shop" className="inline-flex items-center gap-2 text-ape-muted hover:text-ape-red text-sm font-head uppercase tracking-widest transition-colors duration-150" data-testid="back-to-shop">
          <ArrowLeft size={16} /> Back to Shop
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-2 gap-12">
        {/* image */}
        <div className="border border-ape-border relative overflow-hidden">
          <div className="h-1.5 w-full" style={{ backgroundColor: p.accent }} />
          <div className="aspect-square bg-gradient-to-b from-[#101010] to-[#1c1c1c] flex items-center justify-center relative">
            <div className="absolute inset-0 opacity-25" style={{ background: `radial-gradient(circle at 50% 45%, ${p.accent}, transparent 60%)` }} />
            {p.image
              ? <img src={p.image} alt={p.name} className="h-full w-full object-contain p-6 relative z-10" />
              : <Vial accent={p.accent} label={p.dosage} size="lg" />}
          </div>
        </div>

        {/* details */}
        <div>
          <div className="flex items-center gap-2 font-mono2 text-xs tracking-widest text-ape-muted mb-3">
            <span className="border border-ape-border px-2 py-1" style={{ color: p.accent }}>{p.category.toUpperCase()}</span>
            <span className="flex items-center gap-1"><ShieldCheck size={13} className="text-ape-red" /> BATCH VERIFIED</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-none">{title}</h1>
          <p className="text-ape-muted mt-2 font-head uppercase tracking-wide text-sm">{p.subtitle}</p>

          {grouped && (
            <div className="mt-6" data-testid="detail-variant-selector">
              <div className="font-mono2 text-[10px] tracking-widest text-ape-muted mb-2">SELECT SIZE</div>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button key={v.slug} onClick={() => nav(`/product/${v.slug}`)} data-testid={`detail-variant-${v.slug}`}
                    className={`font-mono2 text-sm tracking-widest px-4 py-2 border transition-colors duration-150 ${v.slug === p.slug ? "bg-ape-red border-ape-red text-white" : "border-ape-border text-ape-muted hover:border-white hover:text-white"}`}>
                    {v.dosage} · ${v.price.toFixed(0)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-baseline gap-4 mt-6">
            <span className="font-mono2 text-4xl font-bold" data-testid="detail-price">${p.price.toFixed(2)}</span>
            <span className="font-mono2 text-sm text-ape-muted">{p.dosage} · {p.form}</span>
          </div>

          <p className="text-white/90 mt-6 leading-relaxed">{p.description}</p>

          <div className="grid grid-cols-3 gap-3 mt-6">
            {[{ l: "Purity", v: p.purity }, { l: "Dosage", v: p.dosage }, { l: "Form", v: p.form.split(" ")[0] }].map((s, i) => (
              <div key={i} className="border border-ape-border bg-ape-surface p-4 text-center">
                <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">{s.l.toUpperCase()}</div>
                <div className="font-display text-xl mt-1" style={{ color: p.accent }}>{s.v}</div>
              </div>
            ))}
          </div>

          {/* qty + cart */}
          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border border-ape-border">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrease" className="p-3 hover:text-ape-red transition-colors duration-150"><Minus size={16} /></button>
              <span className="w-12 text-center font-mono2 font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-increase" className="p-3 hover:text-ape-red transition-colors duration-150"><Plus size={16} /></button>
            </div>
            <button onClick={() => { addItem(p, qty); toast.success(`${qty} × ${p.name} added`); }} data-testid="detail-add-to-cart"
              className="flex-1 bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest py-3.5 transition-colors duration-150">
              Add to Cart
            </button>
          </div>
          <button onClick={buyNow} data-testid="detail-buy-now"
            className="w-full mt-3 border-2 border-ape-red text-ape-red hover:bg-ape-red hover:text-white font-head uppercase tracking-widest py-3.5 transition-colors duration-150">
            Buy Now
          </button>

          <div className="mt-6 space-y-2 text-sm text-ape-muted">
            <div className="flex items-center gap-2"><Truck size={16} className="text-ape-red" /> Discreet, secure shipping on all orders</div>
            <div className="flex items-center gap-2"><FlaskConical size={16} className="text-ape-red" /> Independently third-party lab tested</div>
          </div>

          <div className="mt-6 bg-ape-red/10 border border-ape-red p-4 font-mono2 text-xs tracking-widest text-ape-red">
            {LEGAL}
          </div>
        </div>
      </div>

      {/* Research areas + batches */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-24 grid lg:grid-cols-2 gap-8">
        <div className="border border-ape-border bg-ape-surface p-8">
          <div className="flex items-center gap-2 mb-5"><Beaker className="text-ape-red" size={22} /><h3 className="font-display text-2xl tracking-wide">RESEARCH AREAS</h3></div>
          <ul className="space-y-3">
            {p.research_areas.map((r, i) => (
              <li key={i} className="flex items-center gap-3 text-sm"><CheckCircle2 size={18} style={{ color: p.accent }} /> {r}</li>
            ))}
          </ul>
        </div>
        <div className="border border-ape-border bg-ape-surface p-8">
          <div className="flex items-center gap-2 mb-5"><ShieldCheck className="text-ape-red" size={22} /><h3 className="font-display text-2xl tracking-wide">BATCH VERIFICATION</h3></div>
          <p className="text-ape-muted text-sm mb-4">Verified batches available for this compound. Enter a code on the verification page to view the full certificate of analysis.</p>
          <div className="flex flex-wrap gap-2">
            {p.batches.map((b) => (
              <Link key={b} to={`/verify?batch=${b}`} className="font-mono2 text-xs border border-ape-border px-3 py-2 hover:border-ape-red hover:text-ape-red transition-colors duration-150" data-testid={`batch-link-${b}`}>
                {b}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
